
import { useState, useEffect, useRef } from "react"
import { Container, Row, Col, Card, Button, Spinner, Form } from "react-bootstrap"
import { AlertCircle, BookOpen, GraduationCap, ChevronRight, BarChart2, Search, User } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { getStudentProfileByUserId } from "../../../../services/StudentProfileService"
import { getBatchById } from "../../../../services/BatchService"
import { toast } from '../../common/Toast/toast';
import { useAuth } from "../../../../context/AuthContext"

const DisplayEnrolledCourse = () => {
  const [courses, setCourses] = useState([])
  const [filteredCourses, setFilteredCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [studentProfile, setStudentProfile] = useState(null)
  const navigate = useNavigate()

  // Direct student ID for testing - replace with auth when ready
  const currentStudentId = "685d739d8d945b40bb152eab" // This should come from your auth system later

  // TEMPORARY: Hardcoded batch IDs for testing - remove when API is deployed with batchId
  const tempBatchIds = [
    "685fc586543bb3239a15b587",
    "685d7aed543bb3239a15b585"
  ]

  const fetchStudentCourses = async () => {
    try {
      setLoading(true)

      // Get student profile directly by ID
      const profileResponse = await getStudentProfileByUserId(currentStudentId)
      const profile = profileResponse.data
      setStudentProfile(profile)

      if (!profile) {
        throw new Error("Student profile not found")
      }

      // TEMPORARY: Using hardcoded batch IDs for testing (supports multiple batches)
      const batchPromises = tempBatchIds.map(async (batchId) => {
        try {
          const batchResponse = await getBatchById(batchId)
          return batchResponse.data
        } catch (error) {
          console.error(`Error fetching batch ${batchId}:`, error)
          return null
        }
      })

      const batches = await Promise.all(batchPromises)
      const validBatches = batches.filter((batch) => batch !== null)

      // Extract unique courses from all batches
      const allCourses = []
      validBatches.forEach((batch) => {
        if (batch.courseId && Array.isArray(batch.courseId)) {
          batch.courseId.forEach((course) => {
            const existingCourse = allCourses.find((c) => c.id === course.id)
            if (!existingCourse) {
              allCourses.push(course)
            }
          })
        }
      })

      setCourses(allCourses)
      setFilteredCourses(allCourses)
      setError(null)
    } catch (err) {
      setError("Failed to fetch your courses. Please try again later.")
      console.error("Error fetching student courses:", err)
      toast.error("Failed to load your courses")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStudentCourses()
  }, [])

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredCourses(courses)
    } else {
      const filtered = courses.filter(
        (course) =>
          course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          course.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          course.code.toLowerCase().includes(searchTerm.toLowerCase()),
      )
      setFilteredCourses(filtered)
    }
  }, [searchTerm, courses])

  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();
  useEffect(() => {
    if (!hasPermission('student-id-card', 'view','lms')) {
      if (!hasShownToast.current) {
        toast.error('You do not have permission to add departments.');
        navigate('/');
        console.log("hello")
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);


  const handleViewMCQs = (courseId, courseName) => {
    console.log(`Viewing MCQs for course: ${courseName} (ID: ${courseId})`)
    navigate(`/student-mcq-view/${courseId}?courseName=${encodeURIComponent(courseName)}`)
  }

  if (loading) {
    return (
      <div className="min-vh-100 bg-orange-50">
        <Container fluid>
          <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 text-center">
            <div className="position-relative mb-4">
              <Spinner 
                animation="grow" 
                variant="warning" 
                style={{ width: "5rem", height: "5rem" }} 
              />
              <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
                <BookOpen size={32} className="text-white" />
              </div>
            </div>
            <h4 className="mt-4 text-orange-800 fw-bold">Loading Your Courses</h4>
            <p className="text-orange-600">Preparing your learning dashboard</p>
          </div>
        </Container>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-vh-100 bg-gradient-orange">
        <Container fluid>
          <div className="d-flex flex-column align-items-center justify-content-center min-vh-100">
            <div
              className="p-5 rounded-4 shadow-lg text-center bg-white"
              style={{ maxWidth: "500px" }}
            >
              <div
                className="bg-orange-100 rounded-circle d-flex align-items-center justify-content-center mx-auto mb-4"
                style={{ width: "100px", height: "100px" }}
              >
                <AlertCircle size={48} className="text-orange-500" strokeWidth={1.5} />
              </div>
              <h3 className="text-dark mb-3 fw-bold">Connection Error</h3>
              <p className="text-muted mb-4 fs-5">{error}</p>
              <Button
                onClick={fetchStudentCourses}
                variant="warning"
                className="fw-bold px-4 py-3 rounded-pill text-white"
                size="lg"
              >
                Retry Connection
              </Button>
            </div>
          </div>
        </Container>
      </div>
    )
  }

  return (
    <div className="min-vh-100 ">      {/* Hero Section */}
      <div className="bg-gradient-orange py-3">
        <Container>
          <div className="text-center">
            <div className="d-flex flex-column align-items-center justify-content-center mb-4">
              <div className="position-relative mb-4">
                <div className="bg-orange-100 rounded-circle p-3 d-inline-flex align-items-center justify-content-center">
                  <GraduationCap
                    size={48}
                    className="text-orange-600"
                    strokeWidth={1.5}
                  />
                </div>
              </div>
              <h1 className="display-5 fw-bold text-white mb-3">Interview Preparation</h1>
              <p className="lead text-white mb-4">Master your courses through practice MCQs</p>
              {studentProfile && (
                <div className="bg-white bg-opacity-20 rounded-pill px-4 py-2 backdrop-blur-sm">
                  <div className="d-flex align-items-center text-white">
                    <User size={18} className="me-2 text-orange-700" />
                    <span className="fw-semibold text-orange-700">
                      Welcome, {studentProfile.firstName} {studentProfile.lastName}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Container>
      </div>

      {/* Main Content */}
      <Container className="py-5">
        {/* Search Bar */}
        <Row className="mb-5">
          <Col md={8} className="mb-3 mb-md-0">
            <div className="position-relative">
              <Form.Control
                type="text"
                placeholder="Search courses by name, description or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="ps-5 rounded-pill border-0 shadow-sm py-3"
              />
              <Search size={20} className="position-absolute top-50 start-0 translate-middle-y ms-3 text-orange-500" />
            </div>
          </Col>
          <Col md={4} className="d-flex align-items-center justify-content-md-end">
            <div className="bg-white rounded-pill px-4 py-2 shadow-sm border border-orange-200">
              <span className="text-orange-700 me-2 fw-medium">Your Courses:</span>
              <span className="fw-bold text-orange-600 fs-5">{filteredCourses.length}</span>
            </div>
          </Col>
        </Row>

        {/* Stats Bar */}
        <Row className="mb-5 g-4">
          <Col md={6}>
            <Card className="border-0 shadow-sm h-100 rounded-4 overflow-hidden">
              <Card.Body className="d-flex align-items-center p-4">
                <div className="bg-orange-100 rounded-3 p-3 me-4">
                  <BookOpen size={28} className="text-orange-600" />
                </div>
                <div>
                  <h6 className="text-muted mb-1">Enrolled Courses</h6>
                  <h3 className="mb-0 fw-bold text-orange-600">{courses.length}</h3>
                </div>
              </Card.Body>
              <div className="bg-orange-500 bg-opacity-10 w-100" style={{ height: "4px" }}></div>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="border-0 shadow-sm h-100 rounded-4 overflow-hidden">
              <Card.Body className="d-flex align-items-center p-4">
                <div className="bg-orange-100 rounded-3 p-3 me-4">
                  <BarChart2 size={28} className="text-orange-600" />
                </div>
                <div>
                  <h6 className="text-muted mb-1">Available for Practice</h6>
                  <h3 className="mb-0 fw-bold text-orange-600">{filteredCourses.length}</h3>
                </div>
              </Card.Body>
              <div className="bg-orange-500 bg-opacity-10 w-100" style={{ height: "4px" }}></div>
            </Card>
          </Col>
        </Row>

        {/* Courses Grid */}
        <Row className="g-4 mb-4">
          {filteredCourses.length > 0 ? (
            filteredCourses.map((course) => (
              <Col key={course.id} xl={3} lg={4} md={6}>
                <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden course-card ">
                  <Card.Body className="p-4 d-flex flex-column">
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-orange-100 rounded-3 p-2 me-3">
                        <BookOpen size={20} className="text-orange-600" />
                      </div>
                      <h5 className="fw-bold mb-0 text-truncate">{course.name}</h5>
                    </div>

                    <div className="mb-2 flex-grow-1">
                      <p className="text-muted medium mb-0 line-clamp-3"><span className="fw-bold">Course :</span> {course.description}</p>
                      <p className="text-muted medium mb-0 line-clamp-3"><span className="fw-bold">Course Code :</span> {course.code}</p>
                      <p className="text-muted medium mb-0 line-clamp-3"><span className="fw-bold">Course :</span> {course.duration}</p>

                    </div>

                    <Button
                      onClick={() => handleViewMCQs(course.id, course.name)}
                      variant="warning"
                      className="w-100 fw-bold py-2 rounded-pill d-flex align-items-center justify-content-center text-white gradient-orange-btn border-0"
                    >
                      Practice MCQs
                      <ChevronRight size={20} className="ms-2" />
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))
          ) : (
            <Col xs={12}>
              <div className="text-center py-5">
                <div
                  className="bg-orange-100 rounded-circle d-flex align-items-center justify-content-center mx-auto mb-4"
                  style={{ width: "120px", height: "120px" }}
                >
                  <BookOpen size={48} className="text-orange-600" />
                </div>
                <h3 className="text-dark mb-3 fw-bold">No Courses Found</h3>
                <p className="text-muted mb-4 fs-5">
                  {searchTerm ? "No courses match your search criteria." : "You are not enrolled in any courses yet."}
                </p>
                {searchTerm && (
                  <Button
                    onClick={() => setSearchTerm("")}
                    variant="outline-warning"
                    className="fw-bold px-4 py-2 rounded-pill text-orange-600 border-orange-600"
                    size="lg"
                  >
                    Clear Search
                  </Button>
                )}
              </div>
            </Col>
          )}
        </Row>
      </Container>

      {/* Custom CSS */}
      <style>{`
        .bg-gradient-orange {
          background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
        }
        
        .gradient-orange-btn {
          background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
          transition: all 0.3s ease;
        }
        
        .gradient-orange-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(249, 115, 22, 0.4);
        }
        
        .course-card {
          transition: all 0.3s ease;
          border: 1px solid rgba(249, 115, 22, 0.1);
        }
        
        .course-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 24px rgba(249, 115, 22, 0.1) !important;
          border-color: rgba(249, 115, 22, 0.3);
        }
        
        .rounded-4 {
          border-radius: 1rem !important;
        }
        
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .backdrop-blur-sm {
          backdrop-filter: blur(4px);
        }
        
        .text-orange-50 { color: #fff7ed; }
        .text-orange-100 { color: #ffedd5; }
        .text-orange-200 { color: #fed7aa; }
        .text-orange-300 { color: #fdba74; }
        .text-orange-400 { color: #fb923c; }
        .text-orange-500 { color: #f97316; }
        .text-orange-600 { color: #ea580c; }
        .text-orange-700 { color: #c2410c; }
        .text-orange-800 { color: #9a3412; }
        .text-orange-900 { color: #7c2d12; }
        
        .bg-orange-50 { background-color: #fff7ed; }
        .bg-orange-100 { background-color: #ffedd5; }
        .bg-orange-200 { background-color: #fed7aa; }
        .bg-orange-300 { background-color: #fdba74; }
        .bg-orange-400 { background-color: #fb923c; }
        .bg-orange-500 { background-color: #f97316; }
        .bg-orange-600 { background-color: #ea580c; }
        .bg-orange-700 { background-color: #c2410c; }
        .bg-orange-800 { background-color: #9a3412; }
        .bg-orange-900 { background-color: #7c2d12; }
        
        .border-orange-200 { border-color: #fed7aa; }
        .border-orange-600 { border-color: #ea580c; }
      `}</style>
    </div>
  )
}

export default DisplayEnrolledCourse